/**
 * NextActionPanel
 * 
 * Prominently displays the next executable signal in the Exo Panel.
 * 
 * UX Principle #3: "Next" Is Sacred
 * - What is executable now
 * - Why something is blocked
 * - What the system is waiting on
 * 
 * UX Principle #2: Execution Mode is Default
 * - Strong "Next" emphasis
 * - Minimal context by default
 * - Binary confirmation actions
 */

import React, { useState } from 'react';
import { ExecutionSignalCard } from './ExecutionSignalCard';
import { ExoPanelStatusIndicator } from './ExoPanelStatusIndicator';
import { useExecutionSignals } from './useExecutionSignals';
import { ExecutionSignal, BlockReason } from '../../types/executionSignal';
import { Spin } from 'antd';
import { ReloadOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';

interface NextActionPanelProps {
  /** Callback when a signal is resolved */
  onSignalResolved?: (signal: ExecutionSignal, resolutionType: string) => void;
  /** Whether to show the queue indicator */
  showQueueIndicator?: boolean;
  /** Whether to show the refresh button */
  showRefresh?: boolean;
  /** Enable auto-refresh */
  autoRefresh?: boolean;
  /** Compact mode for smaller spaces */
  compact?: boolean;
}

export const NextActionPanel: React.FC<NextActionPanelProps> = ({
  onSignalResolved,
  showQueueIndicator = true,
  showRefresh = true,
  autoRefresh = false,
  compact = false
}) => {
  const {
    currentSignal,
    pendingSignals,
    queueStatus,
    panelStatus,
    isLoading,
    isResolving,
    error,
    refreshSignals,
    completeSignal,
    blockSignal,
    escalateSignal,
    requestClarification
  } = useExecutionSignals({ autoRefresh });

  const [showQueue, setShowQueue] = useState(false);

  // Handle signal completion
  const handleComplete = async (signalId: string, notes?: string) => {
    await completeSignal(signalId, notes);
    if (currentSignal) {
      onSignalResolved?.(currentSignal, 'completed');
    }
  };

  // Handle signal blocking
  const handleBlock = async (signalId: string, reason: BlockReason, notes?: string) => {
    await blockSignal(signalId, reason, notes);
    if (currentSignal) {
      onSignalResolved?.(currentSignal, 'blocked');
    }
  };

  // Handle signal escalation
  const handleEscalate = async (signalId: string, reason: string, toRole?: string) => {
    await escalateSignal(signalId, reason, toRole);
    if (currentSignal) {
      onSignalResolved?.(currentSignal, 'escalated');
    }
  };

  // Handle clarification request
  const handleClarify = async (signalId: string, question: string) => {
    await requestClarification(signalId, question);
    if (currentSignal) {
      onSignalResolved?.(currentSignal, 'clarification_requested');
    }
  };

  // Render queue indicator
  const renderQueueIndicator = () => {
    if (!showQueueIndicator || queueStatus.total_pending <= 1) return null;

    return (
      <button
        onClick={() => setShowQueue(!showQueue)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '6px',
          color: '#3b82f6',
          fontSize: '13px',
          cursor: 'pointer',
          marginBottom: '12px',
          width: '100%',
          justifyContent: 'space-between'
        }}
      >
        <span>
          <strong>{queueStatus.total_pending}</strong> signals pending
          {queueStatus.high_priority > 0 && (
            <span style={{ color: '#ef4444', marginLeft: '8px' }}>
              ({queueStatus.high_priority} high priority)
            </span>
          )}
        </span>
        {showQueue ? <UpOutlined /> : <DownOutlined />}
      </button>
    );
  };

  // Render queue preview
  const renderQueuePreview = () => {
    if (!showQueue || pendingSignals.length <= 1) return null;

    // Show signals after the first one
    const queuedSignals = pendingSignals.slice(1, 4);

    return (
      <div style={{
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
        borderRadius: '8px',
        border: '1px solid rgba(0, 0, 0, 0.06)'
      }}>
        <div style={{
          fontSize: '12px',
          fontWeight: 600,
          color: '#6b7280',
          marginBottom: '8px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Coming Next
        </div>
        {queuedSignals.map((signal, index) => (
          <div
            key={signal.id}
            style={{
              padding: '8px 12px',
              backgroundColor: 'white',
              borderRadius: '6px',
              marginBottom: index < queuedSignals.length - 1 ? '6px' : 0,
              fontSize: '13px',
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: signal.priority <= 2 ? '#fef2f2' : '#f3f4f6',
              color: signal.priority <= 2 ? '#dc2626' : '#6b7280',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: 600
            }}>
              {index + 2}
            </span>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {signal.message}
            </span>
          </div>
        ))}
        {pendingSignals.length > 4 && (
          <div style={{
            textAlign: 'center',
            fontSize: '12px',
            color: '#9ca3af',
            marginTop: '8px'
          }}>
            +{pendingSignals.length - 4} more
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{
      padding: compact ? '12px' : '16px',
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e5e7eb'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px'
      }}>
        <h3 style={{
          margin: 0,
          fontSize: compact ? '16px' : '18px',
          fontWeight: 600,
          color: '#111827'
        }}>
          What Must Happen Next
        </h3>
        {showRefresh && (
          <button
            onClick={() => void refreshSignals()}
            disabled={isLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 8px',
              backgroundColor: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '4px',
              color: '#6b7280',
              fontSize: '12px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.5 : 1
            }}
          >
            <ReloadOutlined spin={isLoading} />
            {!compact && 'Refresh'}
          </button>
        )}
      </div>

      {/* Error state */}
      {error && (
        <div style={{
          padding: '12px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          marginBottom: '16px',
          color: '#dc2626',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {/* Loading state */}
      {isLoading && !currentSignal && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '48px',
          color: '#6b7280'
        }}>
          <Spin size="large" />
        </div>
      )}

      {/* No signals state - "Silence is valid" */}
      {!isLoading && !currentSignal && (
        <ExoPanelStatusIndicator
          status={panelStatus}
          compact={compact}
        />
      )}

      {/* Current signal */}
      {currentSignal && (
        <>
          {renderQueueIndicator()}
          {renderQueuePreview()}
          
          <ExecutionSignalCard
            signal={currentSignal}
            onComplete={handleComplete}
            onBlock={handleBlock}
            onEscalate={handleEscalate}
            onClarify={handleClarify}
            isResolving={isResolving}
            compact={compact}
          />
        </>
      )}
    </div>
  );
};
